import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

/* ── Mapbox config ──── */
const MAPBOX_STYLE = 'mapbox://styles/mj-epsilon/ckn1s6a7n2eyi17prjy5els6g';
const BRAND_PINK = '#CC457D';   // design system: --brand-pink  → smart match officers
const BRAND_BLUE_DARK = '#404880'; // design system: --brand-blue-dark → assigned officers
const BRAND_GRAY = '#9ca3af';   // tailwind gray-400 → other officers (not in recommendations)

/* ── Container CSS fixes (from Dispoline NearbyCaregiversMap) ──── */
const mapboxFixStyles = `
.mapboxgl-map {
  width: 100% !important;
  height: 100% !important;
}
.mapboxgl-canvas-container {
  width: 100% !important;
  height: 100% !important;
  position: relative !important;
  overflow: visible !important;
}
.mapboxgl-canvas {
  position: absolute !important;
  left: 0 !important;
  top: 0 !important;
  width: 100% !important;
  height: 100% !important;
}
.officer-marker {
  z-index: 2 !important;
  transform-origin: center !important;
  will-change: transform !important;
  transition: transform 0.15s !important;
  pointer-events: auto !important;
}
.mapboxgl-popup {
  z-index: 3 !important;
  pointer-events: auto !important;
}
.mapboxgl-ctrl-bottom-right,
.mapboxgl-ctrl-bottom-left {
  z-index: 4 !important;
}
`;

/**
 * OfficerLocationMap
 *
 * Displays a Mapbox map with:
 * - Blue marker at job location (center)
 * - Emerald markers for assigned officers
 * - Pink markers for allocatable officers
 * - Route line from selected officer to job
 * - Click officers to select and see details
 *
 * Props:
 *  - jobCoordinates: [lng, lat] — job location
 *  - officers: array of { lat, lng, resourceId, proposedRate, ... }
 *  - selectedOfficer: selected officer object or null
 *  - onSelectOfficer: callback function (officer) => void
 */
export default function OfficerLocationMap({
  jobCoordinates,
  officers = [],
  selectedOfficer,
  onSelectOfficer,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const styleInjectedRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  /* ── Inject CSS fix (once) ──────────────────────────────────── */
  useEffect(() => {
    if (styleInjectedRef.current) return;
    const el = document.createElement('style');
    el.appendChild(document.createTextNode(mapboxFixStyles));
    document.head.appendChild(el);
    styleInjectedRef.current = true;
    return () => {
      document.head.removeChild(el);
    };
  }, []);

  /* ── Clear all markers ──────────────────────────────────────── */
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  }, []);

  // Initialize Mapbox
  useEffect(() => {
    if (!jobCoordinates || !mapContainerRef.current) return;

    const [lng, lat] = jobCoordinates;

    mapboxgl.accessToken = 'pk.eyJ1IjoibWotZXBzaWxvbiIsImEiOiJja24wcWV5eTcwcTY5MnZwazA0cTRxMTlwIn0.powZMmJIS2FoR4JY1DFSGg';

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center: [lng, lat],
      zoom: 11,
      attributionControl: false,
      pitch: 0,
      bearing: 0,
      preserveDrawingBuffer: true,
      fadeDuration: 0,
      renderWorldCopies: false,
      scrollZoom: { speed: 0.7, smooth: true },
    });

    map.on('load', () => {
      // Controls (bottom-right, matching Dispoline pattern)
      map.addControl(new mapboxgl.NavigationControl({ showCompass: true, visualizePitch: false }), 'bottom-right');
      map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');

      // Job location marker (Dispoline pattern)
      map.addSource('job-marker', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [lng, lat] },
              properties: { icon: 'mapbox-marker-icon-blue' },
            },
          ],
        },
      });
      map.addLayer({
        id: 'job-marker-layer',
        type: 'symbol',
        source: 'job-marker',
        layout: { 'icon-image': ['get', 'icon'] },
      });

      // Route line source (updated on selection)
      map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [[lng, lat], [lng, lat]] },
        },
      });
      map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': BRAND_PINK, 'line-width': 2 },
      });

      mapRef.current = map;
      setMapLoaded(true);
    });

    map.on('error', (e) => console.error('Mapbox error:', e));

    /* ── ResizeObserver (from Dispoline) ── */
    const ro = new ResizeObserver(() => mapRef.current?.resize());
    if (mapContainerRef.current) ro.observe(mapContainerRef.current);

    return () => {
      ro.disconnect();
      clearMarkers();
      map.remove();
      mapRef.current = null;
      setMapLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobCoordinates, clearMarkers]);

  // Add / update officer markers when data or map changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !jobCoordinates) return;

    clearMarkers();

    officers.forEach((officer) => {
      if (!officer.lat || !officer.lng) {
        return;
      }

      const isAssigned = Boolean(officer.isAssigned);
      const isOther = Boolean(officer.isOther);
      const markerColor = isAssigned ? BRAND_BLUE_DARK : isOther ? BRAND_GRAY : BRAND_PINK;
      const markerSize = isAssigned ? '13px' : isOther ? '8px' : '10px';

      const el = document.createElement('div');
      el.className = 'officer-marker';
      el.style.backgroundColor = markerColor;
      el.style.width = markerSize;
      el.style.height = markerSize;
      el.style.borderRadius = '50%';
      el.style.border = '2px solid #fff';
      el.style.boxShadow = isAssigned
        ? '0 0 0 2px rgba(64,72,128,0.3), 0 1px 4px rgba(0,0,0,0.3)'
        : isOther
          ? '0 1px 3px rgba(0,0,0,0.2)'
          : '0 0 3px rgba(0,0,0,0.3)';
      el.style.cursor = 'pointer';
      el.style.opacity = isOther ? '0.7' : '1';

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([officer.lng, officer.lat])
        .addTo(map);

      markersRef.current.push(marker);

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const selectedId = selectedOfficer?.resourceId?.toString();
        const currentId = officer.resourceId?.toString();
        onSelectOfficer(
          selectedId === currentId ? null : officer
        );
      });
    });

    /* ── Zoom behaviour: scale markers, hide popups ── */
    const onZoomStart = () => {
      if (popupRef.current) popupRef.current.remove();
      markersRef.current.forEach((m) => {
        m.getElement().style.transform = 'scale(0.8)';
      });
    };
    const onZoomEnd = () => {
      markersRef.current.forEach((m) => {
        m.getElement().style.transform = '';
      });
    };
    const onMoveStart = () => {
      if (popupRef.current) popupRef.current.remove();
    };

    map.on('zoomstart', onZoomStart);
    map.on('zoomend', onZoomEnd);
    map.on('movestart', onMoveStart);

    /* ── Fit map to show ALL points (job + officers) ── */
    const allCoords = [[jobCoordinates[0], jobCoordinates[1]]];
    officers.forEach((officer) => {
      if (officer.lat != null && officer.lng != null) {
        allCoords.push([officer.lng, officer.lat]);
      }
    });

    if (allCoords.length > 1) {
      const bounds = allCoords.reduce(
        (b, c) => b.extend(c),
        new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 600 });
    } else {
      map.flyTo({ center: allCoords[0], zoom: 11.5, duration: 600 });
    }

    return () => {
      map.off('zoomstart', onZoomStart);
      map.off('zoomend', onZoomEnd);
      map.off('movestart', onMoveStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officers, mapLoaded, jobCoordinates]);

  // Sync map when officer selected
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !jobCoordinates) return;

    if (!selectedOfficer?.lat) {
      if (popupRef.current) popupRef.current.remove();
      const routeSrc = map.getSource('route');
      if (routeSrc) {
        // Empty FeatureCollection is valid GeoJSON and renders nothing
        routeSrc.setData({ type: 'FeatureCollection', features: [] });
      }
      return;
    }

    const popup = new mapboxgl.Popup({
      closeButton: true,
      className: 'officer-popup',
      offset: [0, -6],
      maxWidth: '220px',
    });

    const user = selectedOfficer.resourceId?.user_id || {};
    const name = user.firstName
      ? `${user.firstName} ${user.lastName || ''}`.trim()
      : selectedOfficer.firstName
        ? `${selectedOfficer.firstName} ${selectedOfficer.lastName || ''}`.trim()
        : '—';
    const isAssigned = Boolean(selectedOfficer.isAssigned);
    const isOther = Boolean(selectedOfficer.isOther);
    const accent = isAssigned ? BRAND_BLUE_DARK : isOther ? BRAND_GRAY : BRAND_PINK;
    const subtitle = isAssigned
      ? `<div style="margin-top:4px;font-size:12px;color:${BRAND_BLUE_DARK};font-weight:500;">&#10003; Assigned</div>`
      : isOther
        ? `<div style="margin-top:4px;font-size:11px;color:#888;">${selectedOfficer.distanceMiles != null ? Math.round(selectedOfficer.distanceMiles * 10) / 10 + ' mi away' : 'Not in recommendations'}</div>`
        : `<div style="margin-top:4px;font-size:12px;color:#888;">$${(selectedOfficer.proposedRate ?? 0).toFixed(2)}/hr</div>`;

    popup
      .setLngLat([selectedOfficer.lng, selectedOfficer.lat])
      .setHTML(
        `<div style="color:#555;text-align:center;font-family:inherit;">` +
          `<div style="font-weight:600;border-bottom:2px solid ${accent};padding-bottom:3px;">${name}</div>` +
          subtitle +
          `</div>`
      )
      .addTo(map);

    popupRef.current = popup;

    // Update route line
    const routeSrc = map.getSource('route');
    if (routeSrc) {
      const [jobLng, jobLat] = jobCoordinates;
      routeSrc.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [
            [selectedOfficer.lng, selectedOfficer.lat],
            [jobLng, jobLat],
          ],
        },
      });
      map.setPaintProperty('route', 'line-color', accent);
    }

    // Smooth pan
    map.easeTo({
      center: [selectedOfficer.lng, selectedOfficer.lat],
      duration: 400,
    });

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };
  }, [selectedOfficer, mapLoaded, jobCoordinates]);

  return (
    <div
      className="overflow-hidden rounded-xl border border-border shadow-sm"
      style={{ height: '100%', width: '100%', position: 'relative' }}
    >
      <div
        ref={mapContainerRef}
        style={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, height: '100%', width: '100%' }}
        aria-label="Map showing job location and available officers"
      />
    </div>
  );
}
